import type { ReactNode } from 'react';
import type { FieldValues, Path, RegisterOptions, PathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import Field from '@/components/common/Field';

export interface RadioOption {
    value: string;
    label: ReactNode;
    description?: string;
    icon?: ReactNode;
    id?: string;
    disabled?: boolean;
}

export interface RadioFieldProps<TFormValues extends FieldValues = FieldValues> {
    name: Path<TFormValues>;
    options: RadioOption[];
    label?: string;
    rules?: Omit<
        RegisterOptions<TFormValues, Path<TFormValues>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
    defaultValue?: PathValue<TFormValues, Path<TFormValues>>;
    className?: string;
    required?: boolean;
    variant?: 'default' | 'modern';
    labelSize?: 'sm' | 'base' | 'lg';
    labelWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
}

const RadioField = <TFormValues extends FieldValues = FieldValues>({
    name,
    label,
    options,
    defaultValue,
    className,
    required,
    variant = 'default',
    labelSize = 'base',
    labelWeight = 'semibold',
}: RadioFieldProps<TFormValues>) => {
    const { getFieldState, formState, register } = useFormContext<TFormValues>();
    const fieldState = getFieldState(name, formState);

    // Styling configuration for the modern variant
    const isModern = variant === 'modern';
    const containerClassName = isModern
        ? 'mt-5 sm:mt-0 inline-flex justify-start items-end gap-4'
        : 'mt-5 sm:mt-0 grid grid-cols-3 gap-2 pt-1';
    const itemClassName = isModern ? 'h-6 flex justify-start items-center gap-2.5' : 'mb-3 flex items-center';
    const radioClassName = isModern
        ? 'w-3.5 h-3.5 rounded-full border border-neutral-300 text-teal-600 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 checked:border-teal-600 checked:bg-teal-600'
        : 'h-4 w-4 border-gray-300 text-teal-600 focus:ring-teal-500';

    const getLabelClassName = () => {
        if (isModern) {
            const sizeClass = labelSize === 'base' ? 'text-base' : labelSize === 'sm' ? 'text-sm' : 'text-lg';
            const weightClass =
                labelWeight === 'semibold'
                    ? 'font-semibold'
                    : labelWeight === 'bold'
                      ? 'font-bold'
                      : labelWeight === 'medium'
                        ? 'font-medium'
                        : 'font-normal';
            return `text-neutral-800 ${sizeClass} ${weightClass} leading-6`;
        }
        return 'ml-3 block text-sm font-medium text-gray-700';
    };

    const getItemStyle = (disabled?: boolean) => {
        if (isModern && disabled) {
            return { opacity: 0.5 };
        }
        return {
            color: disabled ? 'gray' : 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
        };
    };

    return (
        <Field
            name={name}
            id={name}
            label={label}
            className={className}
            required={required}
            errorElement={fieldState.error?.message}
        >
            <div className={containerClassName}>
                {options.map(option => (
                    <div key={option.value} className={itemClassName} style={getItemStyle(option.disabled)}>
                        <input
                            type="radio"
                            id={`${name}-${option.value}`}
                            value={option.value}
                            defaultChecked={option.value === defaultValue}
                            className={radioClassName}
                            {...register(name)}
                            disabled={option.disabled}
                        />
                        <label htmlFor={`${name}-${option.value}`} className={getLabelClassName()}>
                            {option.label}
                        </label>
                    </div>
                ))}
            </div>
        </Field>
    );
};

export default RadioField;
